export const DECISION_AGENT_SOURCE_CLASSES = {
  observed: 'observed',
  connectorDerived: 'connector-derived',
  public: 'public',
  publicFiling: 'public-filing',
  industryReference: 'industry_reference',
  syntheticDemo: 'synthetic-demo',
  manualAdmin: 'manual-admin',
  modeled: 'modeled',
  // origin/main@c81d3050 literal: packages/lib/src/approved-source-systems.ts:162.
  csvAdminUpload: 'csv-admin-upload',
  // origin/main@c81d3050 literal: packages/lib/src/approved-source-systems.ts:281.
  extrapolated: 'extrapolated',
} as const;

export type DecisionAgentSourceClass =
  (typeof DECISION_AGENT_SOURCE_CLASSES)[keyof typeof DECISION_AGENT_SOURCE_CLASSES];

/**
 * Weakest-first disclosure order over the governed source classes — the ONE
 * precedence authority. An answer assembled from several governed sources is
 * only as approved as its WEAKEST input, so that is the class it discloses.
 * Provenance-only runtime labels used by the answer path are included here
 * too, so they cannot accidentally rank strongest by omission. Anything still
 * unlisted ranks weakest/unknown; an unknown class may lower disclosure, but
 * it may never upgrade or launder a mixed-authority aggregate.
 */
export const SOURCE_CLASS_DISCLOSURE_ORDER_WEAKEST_FIRST: readonly string[] =
  Object.freeze([
    DECISION_AGENT_SOURCE_CLASSES.syntheticDemo,
    'unverified',
    'asker_stated',
    DECISION_AGENT_SOURCE_CLASSES.industryReference,
    DECISION_AGENT_SOURCE_CLASSES.extrapolated,
    DECISION_AGENT_SOURCE_CLASSES.modeled,
    'source_pending',
    DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
    DECISION_AGENT_SOURCE_CLASSES.csvAdminUpload,
    DECISION_AGENT_SOURCE_CLASSES.observed,
    DECISION_AGENT_SOURCE_CLASSES.public,
    DECISION_AGENT_SOURCE_CLASSES.publicFiling,
    DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
  ]);

function normalizeDisclosureSourceClass(sourceClass: string): string {
  const normalized = sourceClass.trim().toLowerCase();
  return normalized === 'source-pending' ? 'source_pending' : normalized;
}

/** Rank in the weakest-first order; unlisted classes rank weakest/unknown. */
export function sourceClassDisclosureRank(sourceClass: string): number {
  const index = SOURCE_CLASS_DISCLOSURE_ORDER_WEAKEST_FIRST.indexOf(
    normalizeDisclosureSourceClass(sourceClass),
  );
  return index === -1 ? 0 : index;
}

/**
 * PACKAGE SOURCE POSTURE FROM THE CLOSED SOURCE-CLASS ENUM (#5043 review 6/7).
 *
 * Posture used to be decided by a substring test — `/(approved|governed|receipt)/`
 * over `sourceClass + sourceType`. None of the ten canonical classes contains any
 * of those words, so the test could only ever pass on free-text `sourceType`, and
 * it passed on hostile labels like `pre-approved` and `self_governed` while the
 * real class beside it said `synthetic-demo`. Membership in the closed enum is
 * the only thing that decides posture now; nothing else is consulted, and a label
 * that is not a member is not evidence of anything.
 *
 * A class the tenant's own systems produced is receipted. A class that states a
 * MODEL of the business rather than a record of it is modeled, and a modeled
 * member rides only as a disclosed estimate.
 */
export type SourceClassPackagePosture = 'source_receipted' | 'modeled' | 'unknown';

export const SOURCE_CLASS_PACKAGE_POSTURES: Readonly<
  Record<DecisionAgentSourceClass, Exclude<SourceClassPackagePosture, 'unknown'>>
> = Object.freeze({
  [DECISION_AGENT_SOURCE_CLASSES.observed]: 'source_receipted',
  [DECISION_AGENT_SOURCE_CLASSES.connectorDerived]: 'source_receipted',
  [DECISION_AGENT_SOURCE_CLASSES.public]: 'source_receipted',
  [DECISION_AGENT_SOURCE_CLASSES.publicFiling]: 'source_receipted',
  [DECISION_AGENT_SOURCE_CLASSES.manualAdmin]: 'source_receipted',
  [DECISION_AGENT_SOURCE_CLASSES.csvAdminUpload]: 'source_receipted',
  [DECISION_AGENT_SOURCE_CLASSES.modeled]: 'modeled',
  [DECISION_AGENT_SOURCE_CLASSES.syntheticDemo]: 'modeled',
  [DECISION_AGENT_SOURCE_CLASSES.extrapolated]: 'modeled',
  // Not a record of THIS company, so it never carries a company receipt.
  [DECISION_AGENT_SOURCE_CLASSES.industryReference]: 'modeled',
});

/** Posture for one declared class. A non-member is `unknown`, never a receipt. */
export function sourceClassPackagePosture(
  sourceClass: string | null | undefined,
): SourceClassPackagePosture {
  const declared = typeof sourceClass === 'string' ? sourceClass.trim() : '';
  return (
    SOURCE_CLASS_PACKAGE_POSTURES[declared as DecisionAgentSourceClass] ?? 'unknown'
  );
}

/**
 * Posture for a set of cited classes: the WEAKEST any of them carries. One
 * unknown class makes the whole contribution unknown, and one modeled class
 * makes it modeled — an aggregate is never stronger than its weakest input.
 */
export function citedSourceClassPackagePosture(
  sourceClasses: readonly (string | null | undefined)[],
): SourceClassPackagePosture {
  if (sourceClasses.length === 0) return 'unknown';
  const postures = sourceClasses.map(sourceClassPackagePosture);
  if (postures.includes('unknown')) return 'unknown';
  return postures.includes('modeled') ? 'modeled' : 'source_receipted';
}

export type DataLakeSourceClassMapping = Readonly<{
  raw: string;
  canonical: DecisionAgentSourceClass;
}>;

/**
 * Explicit ingestion-boundary normalization. Raw data-lake aliases are not
 * decision-agent source classes and must never be accepted by registration.
 */
export const DATA_LAKE_SOURCE_CLASS_MAPPINGS = {
  connector: {
    raw: 'connector',
    canonical: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
  },
  industryReference: {
    raw: 'industry-reference',
    canonical: DECISION_AGENT_SOURCE_CLASSES.industryReference,
  },
} as const satisfies Readonly<Record<string, DataLakeSourceClassMapping>>;
